import { DestroyRef, Injectable, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Translate } from 'app/common/translate/Translate';

/**
 * Service to configure translations
 */
@Injectable({
  providedIn: 'root',
})
export class TranslateConfigService {
  /**
   * Setting the default language.
   * @param translateService translator service
   */
  constructor(
    private translateService: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.translateService.addLangs(['hu', 'en']);
    if (localStorage.getItem('language')) {
      this.translateService.use(localStorage.getItem('language') as string);
    } else {
      this.translateService.setDefaultLang('hu');
      this.translateService.use('hu');
      localStorage.setItem('language', 'hu');
    }
  }
  private translatedExpression!: string;

  private RegexCheckCurlyBracketsGetText = new RegExp(/[^{}]+(?=})/g);
  private RegexCheckCurlyBracketsEvenIfEmpty = new RegExp(/{{(.*?)}}/);

  /**
   * Returns the language settings
   */
  getCurrentLang(): string {
    return this.translateService.currentLang;
  }

  /**
   * language change handler
   * @param type language type
   */
  changeLanguage(type: string): void {
    this.translateService.use(type);
    localStorage.setItem('language', type);
  }

  /**
   * Returns the translation by key
   * @param key key to translation
   * @param interpolateParams object of [Key: string]: string
   */
  getTranslation(key: string, interpolateParams?: { [key: string]: string }): string {
    this.translateService
      .get(key, interpolateParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translated: string) => {
        this.translatedExpression = translated;
      });
    return this.translatedExpression;
  }

  /**
   * Validates given Translate (should be used everywhere?)
   * - missing translate in the json
   * - interpolateParams:
   *    missing
   *    wrong
   *    not added
   * @param {Array<Translate>} args
   */
  validateTranslate(...args: Array<Translate>): boolean | Array<Object> {
    if (!isDevMode()) {
      return false;
    }

    let result: boolean = false;

    args.forEach((arg) => {
      const { translateKey, interpolateParams } = arg;
      if (!translateKey) {
        return result;
      }

      const translateLine = this.translateService.instant(translateKey);

      if (translateKey === translateLine) {
        // error missing translate
        const errorMessage = `Missing translate line!
        Key: ${arg.translateKey}
        Lang: ${this.getCurrentLang()}`;

        console.warn(errorMessage);

        result = true;
      }

      if (!this.RegexCheckCurlyBracketsEvenIfEmpty.test(translateLine)) {
        if (interpolateParams) {
          // added interpolateParams but there is no need for it

          const errorMessage = `Added interpolateParams but there is no need for it!
          Key: ${arg.translateKey}
          InterpolateParam: ${Object.keys(interpolateParams).join(', ')}`;

          console.warn(errorMessage);

          result = true;
        } else {
          // there is no interpolateParams in the translation
        }
        return result;
      }
      const containsParams = translateLine.match(this.RegexCheckCurlyBracketsGetText);

      if (!interpolateParams) {
        // missing interpolateParams due not added in
        const errorMessage = `Missing interpolateParams due not added in!
        Key: ${translateKey}
        Missing interpolateParams: ${containsParams.join(', ')}`;

        console.warn(errorMessage);

        return true;
      }

      let didCheckContainsParams = false;

      Object.keys(interpolateParams).forEach((interpolateParam, i, array) => {
        if (!didCheckContainsParams) {
          containsParams.forEach((containsParam: string) => {
            if (!array.includes(containsParam)) {
              // Missing interpolateParams
              const errorMessage = `Missing interpolateParams!
              Key: ${translateKey}
              InterpolateParam: ${containsParam}`;

              console.warn(errorMessage);
              result = true;
            }
          });
          didCheckContainsParams = true;
        }

        if (!containsParams.includes(interpolateParam)) {
          // not includes translate interpolateParams (wrong params added in)
          const errorMessage = `Wrong interpolateParams:${interpolateParam} (miss wrote?)
          Key: ${translateKey}
          Good interpolateParams:
          ${containsParams.join(', ')}`;

          console.warn(errorMessage);

          result = true;
        }
      });
      return result;
    });
    return result;
  }
}
